import type { FC } from 'react';
import { PrivacyStyles as styles } from '../css/styles';
import { Helmet } from 'react-helmet-async';

const UserAgreementUS: FC = () => {
    return (
        <>
            <Helmet>
                <title>Terms of Service — First Klass</title>
                <meta name="description" content="Terms of Service for First Klass platform (www.first-klass.ru) — United States" />
            </Helmet>
            <div className={styles.privacyContainer}>
                <h1>Terms of Service</h1>
                <p>
                    These Terms of Service («Terms») are a binding legal agreement between you
                    and the operator of the <strong>First Klass</strong> platform (
                    <strong>www.first-klass.ru</strong>, «we», «us», or «Company»). They govern
                    your access to and use of the Platform and all associated services.
                </p>
                <p>
                    BY ACCESSING OR USING THE PLATFORM, YOU AGREE TO BE BOUND BY THESE TERMS.
                    IF YOU DO NOT AGREE, DO NOT USE THE PLATFORM.
                </p>

                <h2>1. Eligibility</h2>
                <p>
                    1.1. You must be at least 13 years of age to use the Platform. If you are
                    between 13 and 18 years old, you represent that your parent or legal guardian
                    has reviewed and agreed to these Terms on your behalf.
                </p>
                <p>
                    1.2. By using the Platform, you represent and warrant that you have the legal
                    capacity to enter into a binding agreement and that your use complies with
                    all applicable laws.
                </p>

                <h2>2. Definitions</h2>
                <p>
                    <strong>2.1. Platform</strong> — the First Klass web application, mobile
                    applications, and all associated services available at www.first-klass.ru.
                </p>
                <p>
                    <strong>2.2. User</strong> — any person or entity that accesses or uses
                    the Platform under an account.
                </p>
                <p>
                    <strong>2.3. Teacher</strong> — a User who creates, publishes, and delivers
                    educational content to Students via the Platform.
                </p>
                <p>
                    <strong>2.4. Student</strong> — a User who accesses courses or content
                    provided by a Teacher or Learning Organisation through the Platform.
                </p>
                <p>
                    <strong>2.5. Learning Organisation</strong> — an entity using the Platform
                    to administer online learning for multiple Students.
                </p>
                <p>
                    <strong>2.6. Content</strong> — all materials, including text, images, audio,
                    video, and course files, uploaded or created by Users on the Platform.
                </p>
                <p>
                    <strong>2.7. Subscription</strong> — a paid plan providing access to
                    premium Platform features for a specified billing period.
                </p>

                <h2>3. Account Registration and Security</h2>
                <p>
                    3.1. You must register for an account to access most Platform features.
                    You agree to provide accurate and complete information and to keep it current.
                </p>
                <p>
                    3.2. You are solely responsible for maintaining the confidentiality of your
                    login credentials and for all activity that occurs under your account.
                    Notify us immediately if you suspect any unauthorised use.
                </p>
                <p>
                    3.3. We reserve the right to suspend or terminate your account at our
                    discretion, including for violation of these Terms, without liability to you.
                </p>

                <h2>4. Licence Grant</h2>
                <p>
                    4.1. Subject to your compliance with these Terms, we grant you a limited,
                    non-exclusive, non-transferable, revocable licence to access and use the
                    Platform for personal, non-commercial educational purposes.
                </p>
                <p>
                    4.2. You may not sublicense, sell, resell, transfer, or assign any rights
                    in the Platform. All rights not expressly granted are reserved by us.
                </p>

                <h2>5. Acceptable Use Policy</h2>
                <p>5.1. You agree not to use the Platform to:</p>
                <ul>
                    <li>post or transmit Content that is unlawful, harmful, threatening, abusive,
                        defamatory, obscene, or otherwise objectionable;</li>
                    <li>infringe any third-party intellectual property, privacy, or publicity rights;</li>
                    <li>impersonate any person or entity or falsely represent your affiliation;</li>
                    <li>send spam, phishing messages, or other unsolicited bulk communications;</li>
                    <li>introduce viruses, malware, or any harmful code into the Platform;</li>
                    <li>attempt to gain unauthorised access to any system, account, or network;</li>
                    <li>use automated scraping, crawling, or data extraction tools without
                        our prior written permission;</li>
                    <li>violate any applicable federal, state, or local law or regulation.</li>
                </ul>
                <p>
                    5.2. We may monitor Platform usage for compliance and remove Content or
                    suspend accounts that violate this policy, without prior notice.
                </p>

                <h2>6. User Content</h2>
                <p>
                    6.1. You retain ownership of Content you submit to the Platform. By submitting
                    Content, you grant us a worldwide, non-exclusive, royalty-free licence to
                    host, store, reproduce, and display that Content solely as required to operate
                    and improve the Platform.
                </p>
                <p>
                    6.2. You represent and warrant that you have all necessary rights to grant
                    the above licence and that your Content does not violate any third-party
                    rights or applicable law.
                </p>
                <p>
                    6.3. You agree to indemnify and hold harmless the Company, its officers,
                    directors, and employees from any claims, losses, or damages arising from
                    your Content or your use of the Platform.
                </p>

                <h2>7. Intellectual Property</h2>
                <p>
                    7.1. The Platform and all original content, features, and functionality
                    provided by us are owned by or licensed to the Company and are protected
                    by US and international copyright, trademark, and other intellectual
                    property laws.
                </p>
                <p>
                    7.2. You may not copy, modify, distribute, sell, or lease any part of the
                    Platform or its content, nor may you reverse engineer or attempt to extract
                    source code, without our express written permission.
                </p>

                <h2>8. Subscriptions and Payment</h2>
                <p>
                    8.1. Subscription plans, pricing, and included features are described in
                    the «Pricing» section of the Platform. All prices are in US Dollars unless
                    otherwise stated and are exclusive of applicable taxes.
                </p>
                <p>
                    8.2. Subscriptions automatically renew at the end of each billing period.
                    You may cancel auto-renewal at any time through your account settings.
                    Cancellation takes effect at the end of the current billing period.
                </p>
                <p>
                    8.3. All payments are non-refundable except as required by applicable law
                    or as expressly stated in our Refund Policy. We encourage you to use any
                    available free trial before purchasing a Subscription.
                </p>
                <p>
                    8.4. We reserve the right to change subscription pricing with reasonable
                    advance notice. Price changes will not apply to your current billing period.
                </p>

                <h2>9. Disclaimers</h2>
                <p>
                    THE PLATFORM IS PROVIDED «AS IS» AND «AS AVAILABLE» WITHOUT WARRANTIES OF
                    ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF
                    MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
                    WE DO NOT WARRANT THAT THE PLATFORM WILL BE UNINTERRUPTED, ERROR-FREE,
                    OR FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS.
                </p>

                <h2>10. Limitation of Liability</h2>
                <p>
                    TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL THE
                    COMPANY BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL,
                    OR PUNITIVE DAMAGES, INCLUDING LOST PROFITS OR DATA, ARISING FROM YOUR
                    USE OF OR INABILITY TO USE THE PLATFORM, EVEN IF WE HAVE BEEN ADVISED
                    OF THE POSSIBILITY OF SUCH DAMAGES.
                </p>
                <p>
                    OUR TOTAL LIABILITY TO YOU FOR ANY CLAIM ARISING FROM THESE TERMS OR YOUR
                    USE OF THE PLATFORM SHALL NOT EXCEED THE AMOUNT YOU PAID TO US IN THE
                    TWELVE (12) MONTHS PRECEDING THE CLAIM.
                </p>

                <h2>11. Force Majeure</h2>
                <p>
                    We shall not be liable for any failure or delay in performance resulting
                    from causes beyond our reasonable control, including acts of God, natural
                    disasters, war, terrorism, labor disputes, power failures, or government
                    actions.
                </p>

                <h2>12. Governing Law and Dispute Resolution</h2>
                <p>
                    12.1. These Terms shall be governed by and construed in accordance with
                    the laws of the applicable jurisdiction, without regard to conflict of
                    law principles.
                </p>
                <p>
                    12.2. Any dispute arising out of or relating to these Terms shall first
                    be subject to good-faith negotiation. If unresolved within 30 days, the
                    dispute shall be submitted to binding arbitration in accordance with the
                    rules of the American Arbitration Association (AAA), unless you are a
                    consumer entitled to bring claims in your local court.
                </p>
                <p>
                    12.3. <strong>Class Action Waiver:</strong> You agree that any dispute
                    resolution proceedings will be conducted only on an individual basis and
                    not in a class, consolidated, or representative action.
                </p>

                <h2>13. Changes to These Terms</h2>
                <p>
                    We reserve the right to update these Terms at any time. We will notify
                    you of material changes by posting the revised Terms on the Platform and
                    updating the version date below. Your continued use of the Platform after
                    such changes constitutes acceptance of the updated Terms.
                </p>
                <p>
                    <strong>Version date: April 14, 2026</strong>
                </p>
            </div>
        </>
    );
};

export default UserAgreementUS;
